import { useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { useMutation } from '@apollo/client/react';

import { AddUserDocument } from 'src/graphql/actions/user/addUser.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { UserForm, type IUserForm } from 'src/components/User';
import { Button } from '@/components/ui/button';

import { refetchUsers } from '../api';

const AddUserDialog = ({ onSuccess }: { onSuccess: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [addMember] = useMutation(AddUserDocument, {
    onCompleted: () => {
      refetchUsers();
      onSuccess();
      setIsOpen(false);
    },
  });

  const onClose = () => setIsOpen(false);

  const onSubmit = async (dataForm: IUserForm) => {
    try {
      const { data } = await addMember({
        variables: {
          users: [
            {
              name: dataForm.fullName,
              email: dataForm.email,
              role: dataForm.role,
              phone: dataForm.phone,
            },
          ],
        },
      });

      if (data?.addUser?.success) {
        toast.success('Invitation sent!', {
          description: 'They will be invited to the platform and be able to join your app.',
        });
      } else {
        throw new ClientError(data?.addUser?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary">
          <Plus />
          Add organization member
        </Button>
      </DialogTrigger>
      <DialogContent className="md:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-bold">Invite a member to your organization</DialogTitle>
          <DialogDescription>
            They&apos;ll receive an email with instructions to set up their account.
          </DialogDescription>
        </DialogHeader>
        <UserForm onSubmit={onSubmit} onCancel={onClose} btnText="Send invitation" />
      </DialogContent>
    </Dialog>
  );
};

export default AddUserDialog;
